import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  InjectionToken,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MaterialModule } from '@fullyops/shared/material.module';
import { PermissionType } from '@fullyops/legacy/ui/ui-shared/utils/crm-types';
import { I18NextCapPipe } from 'angular-i18next';
import { map, Observable, of } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Location, LocationInput } from '@fullyops/data/locations/location';
import { MomentTimezoneToAngularTimezonePipe } from '@fullyops/shared/pipes/moment-timezone-to-angular-timezone.pipe';
import { SharedModule } from '@fullyops/shared/shared.module';
import { SidenavPortalBridgeService } from '@fullyops/core/sidenav-form-portal-bridge.service';
import {
  LocationFormComponent,
  LocationFormInputs,
} from '@fullyops/features/locations/containers/location-form/location-form.component';
import { first } from 'rxjs/operators';

export const LOCATION_FORM_TOKEN = new InjectionToken<LocationFormInputs>(
  'location-form-inputs',
);

@Component({
  selector: 'fo-location-list',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    I18NextCapPipe,
    MomentTimezoneToAngularTimezonePipe,
    SharedModule,
  ],
  templateUrl: './location-list.component.html',
  styleUrls: ['./location-list.component.scss'],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
  ],
})
export class LocationListComponent implements OnInit {
  @Input({ required: true }) locations$: Observable<Location[]>;

  @Output() create = new EventEmitter<LocationInput>();
  @Output() update = new EventEmitter<{ id: string; input: LocationInput }>();
  @Output() delete = new EventEmitter<string>();

  protected isSingleLocation$: Observable<boolean>;

  private sidenavPortalBridgeService = inject(SidenavPortalBridgeService);

  ngOnInit(): void {
    this.isSingleLocation$ = this.locations$.pipe(
      map((locations) => locations.length === 1),
    );
  }

  openLocationForm(existingLocation: Location) {
    const isPrimarySwitchDisabled$ =
      existingLocation != null
        ? this.isSingleLocation$.pipe(first())
        : of(false);

    isPrimarySwitchDisabled$.subscribe((isPrimarySwitchDisabled) => {
      this.sidenavPortalBridgeService.openPortal(
        LocationFormComponent,
        LOCATION_FORM_TOKEN,
        {
          initialValue: existingLocation,
          showCompany: false,
          primaryLocationSwitchDisabled: isPrimarySwitchDisabled,
          onCancel: () => this.closeForm(),
          onSave: (location: LocationInput) => {
            if (existingLocation == null) {
              this.create.emit(location);
            } else {
              this.update.emit({ id: existingLocation.id, input: location });
            }
          },
          onDelete: () => {
            this.delete.emit(existingLocation.id);
          },
        },
      );
    });
  }

  closeForm() {
    this.sidenavPortalBridgeService.closePortal();
  }

  protected createPerms = [PermissionType.CAN_EDIT_COMPANIES];
}
